/**
 * Tiny fuzzy-match helpers for the search and contact-picker flows.
 *
 * Why: a user searching "Stephen Gorman" should still find the contact stored
 * as "Steven Gorman" (one common-spelling swap away). We want exact / prefix
 * matches to *always* win, and fuzzy matches to act as a graceful fallback so
 * minor typos don't yield "no results."
 *
 * The Levenshtein implementation uses a single rolling row (O(n) memory) and
 * an early-exit when the running minimum exceeds `maxEdits`, so worst-case
 * cost over a typical 5–10k contact list stays well under one frame on a
 * mid-tier phone.
 */

/**
 * Compute the Levenshtein edit distance between two strings, but never
 * compute past `maxEdits` — if the true distance is greater, we return
 * `maxEdits + 1`. This lets callers prune aggressively.
 *
 * Empty strings: dist("", x) === x.length and vice-versa.
 */
export function boundedLevenshtein(a, b, maxEdits) {
  if (a === b) return 0;
  if (!a) return b.length <= maxEdits ? b.length : maxEdits + 1;
  if (!b) return a.length <= maxEdits ? a.length : maxEdits + 1;

  // Quick length-gap rejection: any two strings whose length differs by more
  // than maxEdits can't possibly be within distance maxEdits.
  if (Math.abs(a.length - b.length) > maxEdits) return maxEdits + 1;

  const m = a.length;
  const n = b.length;
  // Rolling row of size n+1.
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      // min of delete, insert, substitute
      let v = prev[j] + 1;
      const ins = curr[j - 1] + 1;
      if (ins < v) v = ins;
      const sub = prev[j - 1] + cost;
      if (sub < v) v = sub;
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    // If the cheapest cell in this row is already over budget, no cell in
    // any later row can come back under it (each step costs ≥ 0).
    if (rowMin > maxEdits) return maxEdits + 1;
    const tmp = prev; prev = curr; curr = tmp;
  }
  return prev[n];
}

/**
 * Choose a reasonable typo-tolerance budget for a query token based on its
 * length. We don't fuzz very short tokens (<3 chars) because the search
 * space explodes and false-positives dominate.
 */
export function maxEditsForToken(qt) {
  if (qt.length < 3) return 0;     // exact / prefix only
  if (qt.length <= 4) return 1;    // small tokens: one edit
  return 2;                         // longer tokens: up to two edits
}

/**
 * Returns true if `queryToken` matches `searchableToken` either as:
 *   - a prefix (already covered by the legacy matcher),
 *   - a digit-substring (for phone-number-style queries), OR
 *   - within Levenshtein distance `maxEdits` of the full target token.
 *
 * The legacy "prefix on tokens" rule still runs first so exact and partial
 * matches always rank above fuzzy ones (callers can still ORDER results
 * separately if they want explicit ranking).
 */
export function fuzzyTokenMatch(queryToken, searchableToken, maxEdits) {
  if (!queryToken || !searchableToken) return false;
  if (searchableToken.startsWith(queryToken)) return true;
  const isDigits = /^\d+$/.test(queryToken);
  if (isDigits && searchableToken.includes(queryToken)) return true;
  if (maxEdits <= 0) return false;
  // For typo-tolerance we only consider tokens with length close to the
  // query (otherwise a 2-char query would fuzz-match everything).
  if (Math.abs(searchableToken.length - queryToken.length) > maxEdits) return false;
  // Asymmetric length guard (session 124d): a real typo substitutes
  // characters and may add/drop ONE letter ("Stephen" ↔ "Steven") — it
  // doesn't drop 25% of what the user typed. Without this guard, a
  // long query like "mcandrew" fuzz-matches the searchable token
  // "andrew" via two deletions, surfacing every "Andrew" contact when
  // the user clearly typed something more specific. Allow the query
  // to be at most ONE character longer than the searchable token; if
  // the user actually wanted that shorter word they would have typed
  // it directly.
  if (queryToken.length > searchableToken.length + 1) return false;
  return boundedLevenshtein(queryToken, searchableToken, maxEdits) <= maxEdits;
}

/**
 * Variant of `fuzzyTokenMatch` that ALSO reports HOW the match was made:
 *   - 'prefix'    → exact prefix or digit-substring hit
 *   - 'fuzzy'    → matched only via bounded Levenshtein
 *   - null       → no match at all
 */
export function classifyTokenMatch(queryToken, searchableToken, maxEdits) {
  if (!queryToken || !searchableToken) return null;
  if (searchableToken.startsWith(queryToken)) return 'prefix';
  const isDigits = /^\d+$/.test(queryToken);
  if (isDigits && searchableToken.includes(queryToken)) return 'prefix';
  if (maxEdits <= 0) return null;
  if (Math.abs(searchableToken.length - queryToken.length) > maxEdits) return null;
  // Same asymmetric length guard as fuzzyTokenMatch — see comment there.
  // Without this, "Did you mean Andrew Greene?" would be suggested when
  // the user typed "mcandrew", because "andrew" is 2 deletions away.
  if (queryToken.length > searchableToken.length + 1) return null;
  return boundedLevenshtein(queryToken, searchableToken, maxEdits) <= maxEdits ? 'fuzzy' : null;
}

/**
 * Decide whether a result set qualifies for a "Did you mean: X?" suggestion
 * and, if so, compute the best correction.
 *
 * Inputs:
 *   - rawQuery: the user's raw search input
 *   - contacts: the matched contacts (already filtered by fuzzy matcher)
 *
 * Returns either `null` (no suggestion to show) or:
 *   {
 *     correctedQuery:  string,           // the suggested query (e.g. "Steven Gorman")
 *     contact:         the best contact, // useful for one-click navigation
 *     score:           number,           // total edit distance across tokens
 *   }
 *
 * Heuristic:
 *   - Only suggest when at least one query token matched ONLY fuzzily
 *     (i.e. no prefix hit on that token). Otherwise the query was already
 *     exact-ish and we'd just be patronising the user.
 *   - Score each fuzzy-matching contact by the SUM of per-token edit
 *     distances against the best-matching searchable token; the lowest
 *     score wins. Ties broken by alphabetic name (stable, predictable).
 */

/**
 * Relevance rank for an active search query: lower = better.
 *  0 exact name/nickname · 1 startsWith · 2 word-boundary prefix ·
 *  3 contains · 4 fuzzy-only. Surfaces the highest-probability EXACT
 * match first instead of burying it under alphabetical order.
 */
export function relevanceScore(query, c) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return 999;
  const first = (c.first_name || '').toLowerCase();
  const last = (c.last_name || '').toLowerCase();
  const full = `${first} ${last}`.trim();
  const nick = (c.nickname || '').toLowerCase();
  const company = (c.work?.company || c.company || '').toLowerCase();
  const exact = [full, first, last, nick].filter(Boolean);
  const cands = [full, first, last, nick, company].filter(Boolean);
  if (exact.includes(q)) return 0;
  if (cands.some((v) => v.startsWith(q))) return 1;
  if (cands.some((v) => v.split(/\s+/).some((w) => w.startsWith(q)))) return 2;
  if (cands.some((v) => v.includes(q))) return 3;
  return 4;
}

export function suggestCorrection(rawQuery, contacts) {
  const q = (rawQuery || '').trim().toLowerCase();
  if (!q || !contacts?.length) return null;
  const qTokens = q.split(/\s+/).filter(Boolean);
  if (!qTokens.length) return null;

  let best = null;
  for (const c of contacts) {
    const nameParts = [c.first_name, c.last_name, c.middle_name, c.nickname]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!nameParts) continue;
    const sTokens = nameParts.split(/[\s@./_\-]+/).filter(Boolean);
    if (!sTokens.length) continue;

    let totalDist = 0;
    let usedFuzzy = false;
    const correctedTokens = [];
    let allMatched = true;
    for (const qt of qTokens) {
      const budget = maxEditsForToken(qt);
      let bestSt = null;
      let bestDist = Infinity;
      let viaPrefix = false;
      for (const st of sTokens) {
        const cls = classifyTokenMatch(qt, st, budget);
        if (cls === 'prefix') {
          if (!viaPrefix || st.length < (bestSt?.length || Infinity)) {
            bestSt = st; bestDist = 0; viaPrefix = true;
          }
        } else if (cls === 'fuzzy' && !viaPrefix) {
          const d = boundedLevenshtein(qt, st, budget);
          if (d < bestDist) { bestSt = st; bestDist = d; }
        }
      }
      if (bestSt === null) { allMatched = false; break; }
      if (!viaPrefix) usedFuzzy = true;
      totalDist += bestDist;
      correctedTokens.push(bestSt);
    }
    if (!allMatched || !usedFuzzy) continue;

    if (!best || totalDist < best.totalDist
        || (totalDist === best.totalDist
            && correctedTokens.join(' ') < best.corrected.join(' '))) {
      best = { contact: c, corrected: correctedTokens, totalDist };
    }
  }

  if (!best) return null;
  // Compose a nicely-cased suggestion preferring `${first} ${last}` when we
  // matched both name parts, otherwise just the joined tokens we found.
  const fn = (best.contact.first_name || '').trim();
  const ln = (best.contact.last_name || '').trim();
  const display = fn || ln ? `${fn}${fn && ln ? ' ' : ''}${ln}` : best.corrected.join(' ');
  return {
    correctedQuery: display,
    contact: best.contact,
    score: best.totalDist,
  };
}
